import {
  datadogRum,
  type RumEvent,
  type RumEventDomainContext,
} from '@datadog/browser-rum';
import React, { useContext, useEffect, useMemo } from 'react';

import type { Environment } from '#src/hooks/environment.tsx';
import { ACCOUNT_SETUP_PATH } from '#src/pages/manage/account-setup/AccountSetupPage.tsx';

export interface DatadogContext {
  setIntegrationPartner: (uuid: unknown) => void;
  setUser: (id: string) => void;
  addError: (error: unknown, context?: object) => void;
}

const noopContext: DatadogContext = {
  setIntegrationPartner: () => {},
  setUser: () => {},
  addError: (error, context) => {
    // eslint-disable-next-line no-console
    console.warn(error, context);
  },
};

const ctx = React.createContext<DatadogContext>(noopContext);

interface DatadogProps {
  children: React.JSX.Element;
  env: Environment;
  version: string;
}

const excludedErrors = new Set([
  "csp_violation: 'eval' blocked by 'script-src' directive",
  "csp_violation: 'wasm-eval' blocked by 'script-src' directive",
  "csp_violation: 'inline' blocked by 'script-src-elem' directive",
  "csp_violation: 'data' blocked by 'media-src' directive",
  "csp_violation: 'blob' blocked by 'worker-src' directive",
]);

const emailPreflightRegex =
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;

/**
 * Crawlers/synthetic monitors generate many `initial_load` views with huge idle
 * time, inflating LCP p95. We can't block them, but tagging lets dashboards scope
 * to real users via `@context.isBot:false`.
 */
const botUserAgentRegex =
  /bot|crawl|spider|slurp|headless|lighthouse|pingdom|pagespeed|gtmetrix|monitor|preview|scan|facebookexternalhit|embedly|prerender|phantom|puppeteer|playwright|python-requests|curl|wget/i;

const isLikelyBot = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  if (navigator.webdriver) {
    return true;
  }
  return botUserAgentRegex.test(navigator.userAgent);
};

const filterExcludedErrors = (
  event: RumEvent,
  _context: RumEventDomainContext,
): boolean => {
  if (event.view.url === ACCOUNT_SETUP_PATH) {
    return false;
  }
  return !(
    event.type === 'error' &&
    (excludedErrors.has(event.error.message) ||
      emailPreflightRegex.test(event.error.message))
  );
};

export const DatadogProvider = ({ children, env, version }: DatadogProps) => {
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const branchVersion = searchParams.get('v');

    if (env === 'prod' && !branchVersion) {
      // https://docs.datadoghq.com/real_user_monitoring/browser/#configuration
      datadogRum.init({
        applicationId: 'afa17fea-d9dc-4410-9240-7038f822de85',
        clientToken: 'pub9f7c0d6c91839e7cb8b9c255a17b0635',
        site: 'datadoghq.com',
        service: 'developer-site',
        env,
        version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        silentMultipleInit: true,
        defaultPrivacyLevel: 'mask',
        beforeSend: filterExcludedErrors,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        workerUrl: '/static/datadog-rum-worker.js',
      });
      datadogRum.setGlobalContextProperty('team', 'indirect-platform');
      datadogRum.setGlobalContextProperty('isBot', isLikelyBot());
    }
  }, [env, version]);

  const value = useMemo(
    () =>
      env === 'prod'
        ? {
            setIntegrationPartner: (uuid: unknown) => {
              datadogRum.setGlobalContextProperty(
                'integrationPartnerUuid',
                uuid,
              );
            },
            setUser: (id: string) => {
              datadogRum.setUser({ id });
            },
            addError: (error: unknown, context?: object) => {
              // eslint-disable-next-line no-console
              console.warn(error, context);
              datadogRum.addError(error, context);
            },
          }
        : noopContext,
    [env],
  );

  return <ctx.Provider value={value}>{children}</ctx.Provider>;
};

export const useDatadog = () => useContext(ctx);
